import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { ImportBatchDetail } from '../../api/types.js';
import { useAuth } from '../../auth/AuthContext.js';
import { formatKc } from '../../lib/format.js';
import { Badge, Chyba, Input, SkeletonSeznam, td, th } from '../../components/ui.js';

/**
 * Obsah ceníku (položky importovaného soupisu prací) — sdílený panel pro
 * detail smlouvy (sekce Ceník) i stránku importů. Read-only: kód, popis, MJ,
 * množství, jednotková cena (za zdí financí), objekt. S rychlým filtrem —
 * soupisy mívají stovky řádků.
 */
export function CenikPolozkyPanel({ batchId }: { batchId: string }) {
  const { maFinance } = useAuth();
  const [hledat, setHledat] = useState('');

  const { data: batch, error } = useQuery({
    queryKey: ['import-batch', batchId],
    queryFn: () => api.get<ImportBatchDetail>(`/api/imports/${batchId}`),
  });

  if (error) return <Chyba>{hlaskaZChyby(error)}</Chyba>;
  if (!batch) return <SkeletonSeznam radku={1} vyska="h-40" />;

  const dotaz = hledat.trim().toLowerCase();
  const polozky = dotaz
    ? batch.polozky.filter(
        (p) => p.kod.toLowerCase().includes(dotaz) || p.popis.toLowerCase().includes(dotaz),
      )
    : batch.polozky;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-primary bg-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="min-w-0 truncate text-base font-semibold text-text-heading">
            Položky ceníku — {batch.nazevSouboru}
          </h3>
          <Badge stav={batch.jePlatny ? 'success' : 'neutral'}>
            {batch.jePlatny ? 'platný' : 'archivní'}
          </Badge>
        </div>
        <span className="w-64 max-w-full">
          <Input
            aria-label="Hledat v ceníku"
            placeholder="Hledat kód nebo popis…"
            className="!min-h-10 !text-sm"
            value={hledat}
            onChange={(e) => setHledat(e.target.value)}
          />
        </span>
      </div>

      {polozky.length === 0 ? (
        <p className="py-4 text-center text-sm text-text-secondary">
          {dotaz ? 'Nic nenalezeno — zkuste jiný kód nebo část popisu.' : 'Ceník je prázdný.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={`${th} w-32`}>Kód</th>
                <th className={th}>Popis</th>
                <th className={`${th} w-16`}>MJ</th>
                <th className={`${th} w-28 text-right`}>Množství</th>
                {maFinance && <th className={`${th} w-32 text-right`}>Jedn. cena</th>}
                <th className={`${th} w-24`}>Objekt</th>
              </tr>
            </thead>
            <tbody>
              {polozky.map((p) => (
                <tr key={p.id} className="hover:bg-bg">
                  <td className={`${td} font-medium whitespace-nowrap`}>{p.kod}</td>
                  <td className={td}>
                    {p.popis}
                    {p.vrn && (
                      <span className="ml-2 rounded bg-fill-disabled px-1.5 py-0.5 text-xs font-semibold text-text-secondary">
                        VRN
                      </span>
                    )}
                  </td>
                  <td className={td}>{p.jednotka}</td>
                  <td className={`${td} text-right whitespace-nowrap`}>{p.mnozstvi}</td>
                  {maFinance && (
                    <td className={`${td} text-right whitespace-nowrap`}>
                      {formatKc(p.jednotkovaCena)}
                    </td>
                  )}
                  <td className={td}>{p.objekt?.kod ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-text-secondary">
        {polozky.length}
        {dotaz ? ` z ${batch.polozky.length}` : ''} položek · ceník je read-only (smluvní opora ZL)
        · ceny netto bez DPH.
      </p>
    </section>
  );
}
